import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.js';
import {
  useBlog, useCategories, usePosts, useTags, useTemplates, useInvalidate, useMutation, useQuery,
  type Category, type Blog,
} from '../../lib/queries.js';
import {
  Badge, Button, Card, ColorInput, ConfirmDialog, EmptyState, ErrorBox, Input, Loading,
  Modal, PageHeader, Pagination, Select, Tabs, Textarea, Toggle, STATUS_TONE, useToast, cx,
  IconPencil, IconFolder,
  IconExternal, IconEye, IconPlus,
} from '../../components/ui/index.js';
import { STATUS_LABEL, date, num, relative } from '../../lib/format.js';
import { hasRole, useRole } from '../../lib/auth.js';
import { PagePreviewModal } from '../../components/PagePreviewModal.js';

// ============================== Posts tab ==============================

function PostsTab({ blog }: { blog: Blog }) {
  const [previewPost, setPreviewPost] = useState<{ title: string; url: string } | null>(null);
  const role = useRole();
  const [filters, setFilters] = useState({ status: '', categoryId: '', q: '', page: 1, sort: 'recent' });
  const posts = usePosts(blog.id, filters);
  const categories = useCategories(blog.id);

  const set = (patch: Partial<typeof filters>) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1">
          <Input placeholder="ค้นหาจากหัวข้อหรือคำโปรย…" value={filters.q}
                 onChange={(e) => set({ q: e.target.value })} />
        </div>
        <Select value={filters.status} onChange={(e) => set({ status: e.target.value })} className="w-auto">
          <option value="">ทุกสถานะ</option>
          {['PUBLISHED', 'DRAFT', 'IN_REVIEW', 'SCHEDULED', 'ARCHIVED'].map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </Select>
        <Select value={filters.categoryId} onChange={(e) => set({ categoryId: e.target.value })} className="w-auto">
          <option value="">ทุกหมวดหมู่</option>
          {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select value={filters.sort} onChange={(e) => set({ sort: e.target.value })} className="w-auto">
          <option value="recent">แก้ไขล่าสุด</option>
          <option value="oldest">เก่าสุด</option>
          <option value="popular">ยอดอ่านมากสุด</option>
          <option value="title">เรียงตามชื่อ</option>
        </Select>
        {hasRole(role, 'AUTHOR') && (
          <Link to={`/blogs/${blog.id}/posts/new`}><Button>+ เขียนบทความ</Button></Link>
        )}
      </div>

      {posts.isLoading ? <Loading /> : !posts.data?.data.length ? (
        <EmptyState
          icon={<IconPencil size={22} />} title={filters.q || filters.status ? 'ไม่พบบทความที่ตรงกับเงื่อนไข' : 'ยังไม่มีบทความ'}
          description={filters.q || filters.status ? 'ลองปรับตัวกรองหรือคำค้นหา' : 'เขียนบทความแรกเพื่อเริ่มใช้งาน widget'}
          action={hasRole(role, 'AUTHOR') && !filters.q && !filters.status && (
            <Link to={`/blogs/${blog.id}/posts/new`}><Button>เขียนบทความแรก</Button></Link>
          )}
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--line)] bg-[var(--sunken)] text-left text-xs uppercase tracking-wide text-[var(--ink-3)]">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">บทความ</th>
                    <th className="px-4 py-2.5 font-semibold">สถานะ</th>
                    <th className="px-4 py-2.5 font-semibold">หมวดหมู่</th>
                    <th className="px-4 py-2.5 text-right font-semibold">ยอดอ่าน</th>
                    <th className="px-4 py-2.5 font-semibold">แก้ไขล่าสุด</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line-soft)]">
                  {posts.data.data.map((p) => (
                    <tr key={p.id} className="hover:bg-[var(--sunken)]">
                      <td className="px-4 py-3">
                        <Link to={`/blogs/${blog.id}/posts/${p.id}`}
                              className="font-medium text-[var(--ink)] hover:text-[var(--brand)]">
                          {p.title}
                        </Link>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--ink-3)]">
                          <span>{p.author.name}</span>
                          {p.isPinned && <Badge tone="violet">ปักหมุด</Badge>}
                          {p.isFeatured && <Badge tone="amber">แนะนำ</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={STATUS_TONE[p.status] ?? 'gray'}>{STATUS_LABEL[p.status] ?? p.status}</Badge>
                        {p.status === 'SCHEDULED' && p.scheduledAt && (
                          <div className="mt-1 text-xs text-[var(--ink-3)]">{date(p.scheduledAt, true)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--ink-2)]">{p.category?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[var(--ink-2)]">{num(p.viewCount)}</td>
                      <td className="px-4 py-3 text-[var(--ink-3)]">{relative(p.updatedAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {p.status === 'PUBLISHED' && (
                            <Button size="sm" variant="ghost" icon={<IconEye size={13} />}
                                    onClick={() => setPreviewPost({ title: p.title, url: p.url })}>ดูตัวอย่าง</Button>
                          )}
                          <Link to={`/blogs/${blog.id}/posts/${p.id}`}>
                            <Button size="sm" variant="ghost">แก้ไข</Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Pagination page={posts.data.meta.page} totalPages={posts.data.meta.totalPages}
                      onChange={(page) => setFilters((f) => ({ ...f, page }))} />
        </>
      )}

      <PagePreviewModal
        open={Boolean(previewPost)}
        onClose={() => setPreviewPost(null)}
        title={previewPost ? `ตัวอย่าง — ${previewPost.title}` : ''}
        url={previewPost?.url ?? null}
        height={600}
        meta={previewPost && <span className="font-mono text-[11px] text-[var(--ink-3)]">{previewPost.url}</span>}
      />
    </div>
  );
}

// ============================ Categories tab ============================

function CategoriesTab({ blog }: { blog: Blog }) {
  const categories = useCategories(blog.id);
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/v1/categories/${id}`),
    onSuccess: () => {
      invalidate(['categories', blog.id], ['posts', blog.id]);
      toast('ลบหมวดหมู่แล้ว — บทความในหมวดนี้ยังอยู่ครบ');
      setDeleting(null);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (categories.isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      {hasRole(role, 'EDITOR') && (
        <div className="flex justify-end"><Button onClick={() => setEditing('new')}>+ เพิ่มหมวดหมู่</Button></div>
      )}

      {!categories.data?.length ? (
        <EmptyState icon={<IconFolder size={22} />} title="ยังไม่มีหมวดหมู่"
                    description="หมวดหมู่ช่วยให้ผู้อ่านหาบทความได้ง่ายขึ้น และใช้กรองบทความใน widget ได้"
                    action={hasRole(role, 'EDITOR') && <Button onClick={() => setEditing('new')}>เพิ่มหมวดหมู่แรก</Button>} />
      ) : (
        <Card className="divide-y divide-[var(--line-soft)]">
          {categories.data.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3">
              <span className="h-3 w-3 shrink-0 rounded-full ring-2 ring-inset ring-black/5"
                    style={{ background: c.color ?? '#CBD5E1' }} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--ink)]">{c.name}</p>
                <p className="truncate font-mono text-xs text-[var(--ink-3)]">/c/{c.slug}</p>
              </div>
              <span className="shrink-0 text-xs text-[var(--ink-3)]">{c.postCount} บทความ</span>
              {hasRole(role, 'EDITOR') && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>แก้ไข</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(c)}>ลบ</Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {editing && <CategoryModal blog={blog} category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={Boolean(deleting)} onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending} danger confirmLabel="ลบหมวดหมู่"
        title="ลบหมวดหมู่?"
        message={<>ลบหมวดหมู่ <strong>{deleting?.name}</strong> ใช่หรือไม่? บทความ {deleting?.postCount} รายการในหมวดนี้จะไม่ถูกลบ แต่จะกลายเป็น &ldquo;ไม่มีหมวดหมู่&rdquo;</>}
      />
    </div>
  );
}

function CategoryModal({ blog, category, onClose }: { blog: Blog; category: Category | null; onClose: () => void }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [form, setForm] = useState({
    name: category?.name ?? '',
    description: category?.description ?? '',
    color: category?.color ?? '#1E63E9',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => category
      ? api.patch(`/v1/categories/${category.id}`, form)
      : api.post('/v1/categories', { blogId: blog.id, ...form }),
    onSuccess: () => {
      invalidate(['categories', blog.id]);
      toast(category ? 'บันทึกหมวดหมู่แล้ว' : 'เพิ่มหมวดหมู่แล้ว');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open onClose={onClose} title={category ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่'}
      footer={<>
        <Button variant="ghost" onClick={onClose}>ยกเลิก</Button>
        <Button loading={save.isPending} disabled={!form.name} onClick={() => { setError(null); save.mutate(); }}>บันทึก</Button>
      </>}
    >
      <div className="space-y-4">
        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <Input label="ชื่อหมวดหมู่" required autoFocus value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Textarea label="คำอธิบาย" rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <ColorInput label="สีป้ายหมวดหมู่" value={form.color}
                    onChange={(v) => setForm({ ...form, color: v })}
                    hint="ใช้เป็นสีของป้ายหมวดหมู่ทั้งใน widget และหน้าบล็อก" />
      </div>
    </Modal>
  );
}

// ============================== Theme tab ==============================

function ThemeTab({ blog }: { blog: Blog }) {
  const themes = useTemplates('BLOG_THEME');
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const [themeCode, setThemeCode] = useState(blog.themeCode);
  const [config, setConfig] = useState<Record<string, any>>(blog.themeConfig ?? {});
  const canEdit = hasRole(role, 'ADMIN');

  const save = useMutation({
    mutationFn: () => api.patch(`/v1/blogs/${blog.id}/theme`, { themeCode, themeConfig: config }),
    onSuccess: () => { invalidate(['blog', blog.id], ['blogs']); toast('บันทึกธีมเรียบร้อย'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const dirty = themeCode !== blog.themeCode || JSON.stringify(config) !== JSON.stringify(blog.themeConfig ?? {});
  const set = (patch: Record<string, unknown>) => setConfig((c) => ({ ...c, ...patch }));

  /*
   * URL ตัวอย่างที่พ่วงธีมที่กำลังเลือกอยู่ไปด้วย
   *
   * เดิม iframe ชี้ไปที่ URL บล็อกจริง ซึ่งแสดงเฉพาะธีมที่บันทึกแล้ว —
   * แปลว่าต้องบันทึกทับของจริงก่อนถึงจะเห็นว่าธีมใหม่หน้าตาเป็นยังไง
   * ตอนนี้ blog SSR รับค่า _theme / _themeConfig มา render ทับชั่วคราว
   * (ไม่ลง cache และตั้ง noindex ให้อัตโนมัติ) จึงเห็นผลทันทีที่กดเลือก
   */
  const previewUrl = useMemo(() => {
    const u = new URL(blog.url);
    u.searchParams.set('_theme', themeCode);
    u.searchParams.set('_themeConfig', JSON.stringify(config));
    return u.toString();
  }, [blog.url, themeCode, config]);

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)]">
      <div className="space-y-5">
        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-[var(--ink)]">เลือกธีม</h3>
          <div className="space-y-2">
            {themes.data?.map((t) => (
              <button
                key={t.code} type="button" disabled={!t.available || !canEdit}
                onClick={() => setThemeCode(t.code)}
                className={cx(
                  'w-full rounded-lg border p-3 text-left transition-colors',
                  themeCode === t.code ? 'border-[var(--brand)] bg-[var(--brand-soft)]'
                                       : 'border-[var(--line)]',
                  t.available && canEdit ? 'hover:border-[var(--line)]' : 'cursor-not-allowed opacity-50',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[var(--ink)]">{t.name}</span>
                  {!t.available && <Badge tone="amber">{t.minPlan !== 'FREE' ? t.minPlan : 'Phase 2'}</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-[var(--ink-3)]">{t.description}</p>
              </button>
            ))}
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="text-sm font-semibold text-[var(--ink)]">ปรับแต่ง</h3>
          <ColorInput label="สีหลัก" value={config.primary ?? '#1E63E9'} onChange={(v) => set({ primary: v })} />
          <Select label="ตำแหน่ง sidebar" value={config.sidebarPosition ?? 'right'}
                  onChange={(e) => set({ sidebarPosition: e.target.value })}>
            <option value="right">ด้านขวา</option>
            <option value="left">ด้านซ้าย</option>
            <option value="none">ไม่แสดง</option>
          </Select>
          <Input label="ความมนของมุม (px)" type="number" min={0} max={32}
                 value={config.radius ?? 12} onChange={(e) => set({ radius: Number(e.target.value) })} />
          <Input label="ความกว้างสูงสุด (px)" type="number" min={600} max={1600} step={20}
                 value={config.maxWidth ?? 1200} onChange={(e) => set({ maxWidth: Number(e.target.value) })} />
          <Toggle label="ตรึงแถบเมนูด้านบน" checked={config.stickyHeader !== false}
                  onChange={(v) => set({ stickyHeader: v })} />

          {canEdit && (
            <div className="flex gap-2 pt-1">
              <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>บันทึกธีม</Button>
              {dirty && (
                <Button variant="ghost" onClick={() => { setThemeCode(blog.themeCode); setConfig(blog.themeConfig ?? {}); }}>
                  ยกเลิกการแก้ไข
                </Button>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ข้อ 8.2 — Theme Studio: preview สดข้าง ๆ ตัวปรับแต่ง */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--line)] px-4 py-2.5">
          <span className="text-sm font-medium text-[var(--ink-2)]">ตัวอย่างจริง</span>
          <a href={previewUrl} target="_blank" rel="noopener"><Button size="sm" variant="ghost" icon={<IconExternal size={13} />}>เปิดในแท็บใหม่</Button></a>
        </div>
        {blog.status === 'PUBLISHED' ? (
          <iframe
            key={previewUrl}
            src={previewUrl} title="ตัวอย่างบล็อก" className="h-[640px] w-full border-0"
            sandbox="allow-scripts allow-same-origin"
          />
        ) : (
          <div className="p-10 text-center text-sm text-[var(--ink-3)]">
            บล็อกยังไม่ได้เผยแพร่ — ไปที่แท็บ &ldquo;ตั้งค่า&rdquo; แล้วเปลี่ยนสถานะเป็น &ldquo;เผยแพร่&rdquo; เพื่อดูตัวอย่าง
          </div>
        )}
        {dirty && (
          <div className="border-t border-[color-mix(in_srgb,var(--warn)_35%,transparent)] bg-[var(--warn-soft)] px-4 py-2 text-xs text-[var(--warn)]">
            นี่คือตัวอย่างของธีมที่ยังไม่ได้บันทึก — ผู้อ่านจะยังเห็นธีมเดิมจนกว่าคุณจะกด &ldquo;บันทึกธีม&rdquo;
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================== Domain tab ==============================

type DomainRecord = { type: string; name: string; value: string };
type DomainState = {
  blogId: string; domain: string | null; status: string; urlMode: string;
  verifiedAt: string | null; checkedAt: string | null; error: string | null;
  records: DomainRecord[]; activationPending: boolean;
};

const DOMAIN_STATUS: Record<string, { label: string; tone: 'gray' | 'amber' | 'blue' | 'green' | 'rose' }> = {
  NONE: { label: 'ยังไม่ได้ตั้ง', tone: 'gray' },
  PENDING_DNS: { label: 'รอตั้งค่า DNS', tone: 'amber' },
  DNS_VERIFIED: { label: 'ยืนยัน DNS แล้ว รอเปิดใช้', tone: 'blue' },
  CERT_ISSUING: { label: 'กำลังออกใบรับรอง', tone: 'blue' },
  ACTIVE: { label: 'ใช้งานอยู่', tone: 'green' },
  FAILED: { label: 'มีปัญหา', tone: 'rose' },
};

function DomainTab({ blog }: { blog: Blog }) {
  const toast = useToast();
  const role = useRole();
  const canEdit = hasRole(role, 'ADMIN');
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ['blog-domain', blog.id],
    queryFn: () => api.get<{ data: DomainState }>(`/v1/blogs/${blog.id}/domain`).then((r) => r.data),
  });

  const refresh = () => state.refetch();

  const setDomain = useMutation({
    mutationFn: () => api.post(`/v1/blogs/${blog.id}/domain`, { domain: input.trim() }),
    onSuccess: () => { setInput(''); refresh(); toast('บันทึกโดเมนแล้ว — ตั้งค่า DNS ตามตารางด้านล่างแล้วกดยืนยัน'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const verify = useMutation({
    mutationFn: () => api.post<{ ok: boolean; error?: string }>(`/v1/blogs/${blog.id}/domain/verify`, {}),
    onSuccess: (r) => {
      refresh();
      // ยังตรวจไม่ผ่านไม่ใช่ error ของระบบ — เป็นเรื่องปกติที่ DNS ยังกระจายไม่ทั่ว
      // ถ้าขึ้นเป็นสีแดงทุกครั้งลูกค้าจะคิดว่าตัวเองทำผิดแล้วเลิกกลางทาง
      if (r.ok) toast('ยืนยันโดเมนสำเร็จ');
      else toast(r.error ?? 'ยังตรวจไม่พบเรคคอร์ด — DNS อาจยังกระจายไม่ทั่ว ลองใหม่อีกครั้งใน 5 นาที');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/v1/blogs/${blog.id}/domain`),
    onSuccess: () => { refresh(); toast('ถอดโดเมนออกแล้ว — บล็อกกลับไปใช้ URL เดิม'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const copy = (v: string) => {
    void navigator.clipboard?.writeText(v);
    setCopied(v);
    setTimeout(() => setCopied((c) => (c === v ? null : c)), 1500);
  };

  if (state.isLoading) return <Loading />;
  if (state.error) return <ErrorBox error={state.error} />;
  const d = state.data;
  if (!d) return null;
  const st = DOMAIN_STATUS[d.status] ?? { label: d.status, tone: 'gray' as const };

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[var(--ink)]">โดเมนของคุณเอง</div>
            <p className="mt-1 text-[13px] leading-relaxed text-[var(--ink-2)]">
              ให้บล็อกเปิดด้วยโดเมนของบริษัทคุณ เช่น <code className="font-mono">blog.example.com</code> แทน URL ของ Blogaris
            </p>
          </div>
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>

        {!d.domain && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Input
              className="min-w-[260px] flex-1"
              placeholder="blog.example.com"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!canEdit}
            />
            <Button onClick={() => setDomain.mutate()} disabled={!canEdit || !input.trim() || setDomain.isPending}>
              {setDomain.isPending ? 'กำลังบันทึก…' : 'ตั้งโดเมน'}
            </Button>
          </div>
        )}

        {!d.domain && (
          <p className="mt-3 text-[12px] leading-relaxed text-[var(--ink-3)]">
            ต้องเป็นโดเมนย่อยเท่านั้น เช่น <code className="font-mono">blog.example.com</code> —
            ใช้โดเมนหลัก (<code className="font-mono">example.com</code>) ตรง ๆ ไม่ได้
          </p>
        )}

        {d.domain && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-[var(--sunken)] px-3 py-1.5 font-mono text-sm">{d.domain}</code>
            <Button variant="ghost" onClick={() => remove.mutate()} disabled={!canEdit || remove.isPending}>
              ถอดโดเมน
            </Button>
          </div>
        )}
      </Card>

      {d.records.length > 0 && (
        <Card>
          <div className="text-sm font-semibold text-[var(--ink)]">ตั้งค่า DNS สองรายการนี้</div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--ink-2)]">
            เข้าไปที่ผู้ให้บริการโดเมนของคุณ (ที่เดียวกับที่จดโดเมน) แล้วเพิ่มสองเรคคอร์ดนี้
            จากนั้นกลับมากดยืนยัน — ปกติ DNS ใช้เวลากระจาย 5–30 นาที
          </p>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--line)] text-left text-[12px] text-[var(--ink-3)]">
                  <th className="py-2 pr-4 font-medium">ชนิด</th>
                  <th className="py-2 pr-4 font-medium">ชื่อ</th>
                  <th className="py-2 pr-4 font-medium">ค่า</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {d.records.map((r: DomainRecord) => (
                  <tr key={r.type} className="border-b border-[var(--line)] last:border-0">
                    <td className="py-2.5 pr-4 font-mono text-[13px]">{r.type}</td>
                    <td className="py-2.5 pr-4 font-mono text-[13px] break-all">{r.name}</td>
                    <td className="py-2.5 pr-4 font-mono text-[13px] break-all">{r.value}</td>
                    <td className="py-2.5 text-right">
                      <Button variant="ghost" onClick={() => copy(r.value)}>
                        {copied === r.value ? 'คัดลอกแล้ว' : 'คัดลอก'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={() => verify.mutate()} disabled={!canEdit || verify.isPending}>
              {verify.isPending ? 'กำลังตรวจสอบ…' : 'ยืนยันโดเมน'}
            </Button>
            {d.checkedAt && (
              <span className="text-[12px] text-[var(--ink-3)]">ตรวจล่าสุด {relative(d.checkedAt)}</span>
            )}
          </div>

          {d.error && (
            <p className="mt-3 rounded-lg bg-[var(--sunken)] px-3 py-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
              {d.error}
            </p>
          )}

          {d.activationPending && (
            <p className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
              DNS ถูกต้องแล้ว — ขั้นตอนออกใบรับรอง HTTPS ยังอยู่ระหว่างเปิดใช้งาน
              ระหว่างนี้บล็อกยังเปิดได้ตามปกติที่ URL เดิม เราจะแจ้งเมื่อโดเมนพร้อมใช้
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

// ============================= Settings tab =============================

function SettingsTab({ blog }: { blog: Blog }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const navigate = useNavigate();
  const role = useRole();
  const [form, setForm] = useState({
    title: blog.title,
    description: blog.description ?? '',
    status: blog.status,
    postsPerPage: blog.postsPerPage,
    metaTitle: blog.metaTitle ?? '',
    metaDescription: blog.metaDescription ?? '',
    googleAnalyticsId: blog.googleAnalyticsId ?? '',
    gtmId: blog.gtmId ?? '',
  });
  const [slug, setSlug] = useState(blog.slug);
  const [deleting, setDeleting] = useState(false);

  const canEdit = hasRole(role, 'ADMIN');

  const save = useMutation({
    mutationFn: () => api.patch(`/v1/blogs/${blog.id}`, {
      ...form,
      description: form.description || null,
      metaTitle: form.metaTitle || null,
      metaDescription: form.metaDescription || null,
      googleAnalyticsId: form.googleAnalyticsId || null,
      gtmId: form.gtmId || null,
    }),
    onSuccess: () => { invalidate(['blog', blog.id], ['blogs']); toast('บันทึกการตั้งค่าแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const changeSlug = useMutation({
    mutationFn: () => api.patch(`/v1/blogs/${blog.id}/slug`, { slug }),
    onSuccess: () => {
      invalidate(['blog', blog.id], ['blogs']);
      toast('เปลี่ยน URL แล้ว — ลิงก์เดิมจะถูก redirect ไปยัง URL ใหม่โดยอัตโนมัติ');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/v1/blogs/${blog.id}`),
    onSuccess: () => { invalidate(['blogs'], ['quota']); toast('ลบบล็อกแล้ว'); navigate('/blogs'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <div className="grid max-w-3xl gap-6">
      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">ทั่วไป</h3>
        <Input label="ชื่อบล็อก" value={form.title} disabled={!canEdit}
               onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea label="คำอธิบาย" rows={2} value={form.description} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="สถานะ" value={form.status} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, status: e.target.value as Blog['status'] })}
                  hint="ต้องเป็น 'เผยแพร่' เท่านั้น widget และหน้าบล็อกจึงจะแสดงต่อสาธารณะ">
            <option value="DRAFT">ฉบับร่าง (ยังไม่เปิดสาธารณะ)</option>
            <option value="PUBLISHED">เผยแพร่</option>
            <option value="ARCHIVED">เก็บถาวร</option>
          </Select>
          <Input label="จำนวนบทความต่อหน้า" type="number" min={3} max={48} disabled={!canEdit}
                 value={form.postsPerPage}
                 onChange={(e) => setForm({ ...form, postsPerPage: Number(e.target.value) })} />
        </div>
        {canEdit && <Button loading={save.isPending} onClick={() => save.mutate()}>บันทึก</Button>}
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">SEO</h3>
        <Input label="Meta title" value={form.metaTitle} disabled={!canEdit}
               onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
               hint={`ว่างไว้จะใช้ชื่อบล็อก — ${form.metaTitle.length}/255 ตัวอักษร`} maxLength={255} />
        <Textarea label="Meta description" rows={2} value={form.metaDescription} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                  hint="ความยาวที่เหมาะกับ Google คือ 120–160 ตัวอักษร" maxLength={500} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Google Analytics ID" placeholder="G-XXXXXXXXXX" value={form.googleAnalyticsId}
                 disabled={!canEdit} onChange={(e) => setForm({ ...form, googleAnalyticsId: e.target.value })} />
          <Input label="Google Tag Manager ID" placeholder="GTM-XXXXXXX" value={form.gtmId}
                 disabled={!canEdit} onChange={(e) => setForm({ ...form, gtmId: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <a className="text-[var(--brand)] hover:underline" href={`${blog.url}/sitemap.xml`} target="_blank" rel="noopener">sitemap.xml ↗</a>
          <span className="text-[var(--ink-3)]">·</span>
          <a className="text-[var(--brand)] hover:underline" href={`${blog.url}/rss.xml`} target="_blank" rel="noopener">rss.xml ↗</a>
          <span className="text-[var(--ink-3)]">·</span>
          <a className="text-[var(--brand)] hover:underline" href={`${blog.url}/robots.txt`} target="_blank" rel="noopener">robots.txt ↗</a>
        </div>
        {canEdit && <Button loading={save.isPending} onClick={() => save.mutate()}>บันทึก SEO</Button>}
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">URL ของบล็อก</h3>
        <Input label="Slug" value={slug} disabled={!hasRole(role, 'OWNER')}
               onChange={(e) => setSlug(e.target.value)}
               hint={`URL ปัจจุบัน: ${blog.url}`} />
        {slug !== blog.slug && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-900">
            <p className="font-medium">การเปลี่ยน URL มีผลต่อ SEO</p>
            <p className="mt-1 text-xs">
              ระบบจะสร้าง redirect 301 จาก URL เดิมให้อัตโนมัติ และ widget ที่ฝังอยู่บนเว็บลูกค้า
              จะชี้ไป URL ใหม่เองโดยไม่ต้องแก้โค้ด แต่ควรแจ้ง Google Search Console ด้วย
            </p>
          </div>
        )}
        {hasRole(role, 'OWNER') && (
          <Button loading={changeSlug.isPending} disabled={slug === blog.slug || !slug}
                  onClick={() => changeSlug.mutate()}>เปลี่ยน URL</Button>
        )}
      </Card>

      <Card className="space-y-3 p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">โดเมนของคุณเอง</h3>
        <p className="text-sm text-[var(--ink-3)]">
          ใช้โดเมนของบริษัทคุณเอง เช่น <code className="rounded bg-[var(--sunken)] px-1.5 py-0.5 text-xs">blog.company.co.th</code>
          {' '}แทน URL ของ Blogaris — ฟีเจอร์นี้เปิดให้แพ็กเกจ PRO ขึ้นไปใน Phase 2
        </p>
        <div><Badge tone="amber">เร็ว ๆ นี้</Badge></div>
      </Card>

      {hasRole(role, 'OWNER') && (
        <Card className="border-rose-200 p-5">
          <h3 className="text-sm font-semibold text-[var(--bad)]">พื้นที่อันตราย</h3>
          <p className="mt-1.5 text-sm text-[var(--ink-3)]">
            ลบบล็อกนี้ — บทความและ widget จะถูกซ่อนจากสาธารณะทันที แต่ข้อมูลยังอยู่ในระบบและกู้คืนได้
          </p>
          <Button variant="danger" size="sm" className="mt-3" onClick={() => setDeleting(true)}>ลบบล็อก</Button>
        </Card>
      )}

      <ConfirmDialog
        open={deleting} onClose={() => setDeleting(false)} onConfirm={() => remove.mutate()}
        loading={remove.isPending} danger confirmLabel="ลบบล็อก" title="ลบบล็อกนี้?"
        message={<>บล็อก <strong>{blog.title}</strong> จะถูกซ่อนจากสาธารณะทันที และ widget ทั้งหมดที่ผูกกับบล็อกนี้จะหยุดแสดงผลบนเว็บลูกค้า</>}
      />
    </div>
  );
}

// ============================== Page ==============================

export function BlogDetailPage() {
  const { id } = useParams();
  const blog = useBlog(id);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'posts';

  if (blog.isLoading) return <Loading />;
  if (blog.error) return <ErrorBox error={blog.error} onRetry={() => void blog.refetch()} />;
  if (!blog.data) return null;

  const b = blog.data;

  return (
    <>
      <PageHeader
        breadcrumb={
          <nav className="mb-2 text-sm text-[var(--ink-3)]">
            <Link to="/blogs" className="hover:text-[var(--brand)]">บล็อก</Link>
            <span className="mx-1.5 text-[var(--ink-3)]">/</span>
            <span>{b.title}</span>
          </nav>
        }
        title={b.title}
        description={<a href={b.url} target="_blank" rel="noopener" className="font-mono text-xs hover:text-[var(--brand)]">{b.url} ↗</a>}
        actions={<Badge tone={STATUS_TONE[b.status] ?? 'gray'}>{STATUS_LABEL[b.status] ?? b.status}</Badge>}
      />

      <Tabs
        active={tab}
        onChange={(id) => setParams({ tab: id }, { replace: true })}
        tabs={[
          { id: 'posts', label: 'บทความ', badge: b.stats?.total },
          { id: 'categories', label: 'หมวดหมู่' },
          { id: 'theme', label: 'ธีม' },
          { id: 'domain', label: 'โดเมน' },
          { id: 'settings', label: 'ตั้งค่า' },
        ]}
      />

      <div className="mt-6">
        {tab === 'posts' && <PostsTab blog={b} />}
        {tab === 'categories' && <CategoriesTab blog={b} />}
        {tab === 'theme' && <ThemeTab blog={b} />}
        {tab === 'domain' && <DomainTab blog={b} />}
        {tab === 'settings' && <SettingsTab blog={b} />}
      </div>
    </>
  );
}
